import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import { 
  Play, FileText, CheckCircle2, Clock, Lock, 
  AlertCircle, GraduationCap, Target, Award,
  Calendar, Video, ClipboardList, LayoutGrid, List,
  ChevronLeft, ChevronRight, Shield, Wrench, BookOpen, Users
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PortalDashboard = () => {
  const { user } = useAuth();
  const [cursos, setCursos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, completados: 0, pendientes: 0, porcentaje: 0 });
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cursosRes, catsRes] = await Promise.all([
        axios.get(`${API}/trabajador/mis-cursos`),
        axios.get(`${API}/categorias-capacitacion`),
      ]);
      const cursosData = cursosRes.data;
      setCursos(cursosData);
      setCategorias(catsRes.data);
      
      const completados = cursosData.filter(c => c.completado).length;
      const total = cursosData.length;
      setStats({
        total,
        completados,
        pendientes: total - completados,
        porcentaje: total > 0 ? Math.round((completados / total) * 100) : 0
      });
    } catch (error) {
      toast.error('Error al cargar cursos');
    } finally {
      setLoading(false);
    }
  };

  const CATEGORY_CONFIG = {
    legal: { icon: Shield, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', accent: 'bg-red-100' },
    especifica_cargo: { icon: Wrench, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', accent: 'bg-blue-100' },
    transversal_obligatoria: { icon: BookOpen, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', accent: 'bg-amber-100' },
    transversal_cargo: { icon: Users, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', accent: 'bg-purple-100' },
  };

  // Agrupar cursos por categoría
  const grouped = categorias.map(cat => ({
    ...cat,
    config: CATEGORY_CONFIG[cat.clave] || { icon: GraduationCap, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', accent: 'bg-slate-100' },
    cursos: cursos.filter(c => c.categoria === cat.clave),
  })).filter(g => g.cursos.length > 0);

  // Cursos sin categoría
  const sinCategoria = cursos.filter(c => !c.categoria || !categorias.some(cat => cat.clave === c.categoria));
  if (sinCategoria.length > 0) {
    grouped.push({
      clave: '_sin_categoria', nombre: 'Sin Clasificar', descripcion: '', orden: 99,
      config: { icon: GraduationCap, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', accent: 'bg-slate-100' },
      cursos: sinCategoria,
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const getStatus = (curso) => {
    if (curso.completado) return 'completado';
    if (!curso.puede_tomar) return 'bloqueado';
    return 'pendiente';
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section - Más compacto */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-400 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">¡Hola, {user?.nombre}!</h1>
            <p className="text-teal-100 text-sm">Portal de capacitaciones</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{stats.porcentaje}%</p>
            <p className="text-xs text-teal-100">Completado</p>
          </div>
        </div>
      </div>

      {/* Stats Cards - Más compactos */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Asignados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{stats.completados}</p>
              <p className="text-xs text-slate-500">Completados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{stats.pendientes}</p>
              <p className="text-xs text-slate-500">Pendientes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar - Más compacto */}
      <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
        <span className="text-sm font-medium text-slate-700">Progreso</span>
        <Progress value={stats.porcentaje} className="h-2 flex-1" />
        <span className="text-sm font-bold text-teal-600">{stats.porcentaje}%</span>
      </div>

      {/* Header con controles */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">
          Mis Cursos ({cursos.length})
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm text-teal-600' : 'text-slate-500'}`}
              title="Vista de tarjetas"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm text-teal-600' : 'text-slate-500'}`}
              title="Vista de lista"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Cursos agrupados por categoría */}
      {grouped.map(group => {
        const CatIcon = group.config.icon;
        return (
          <div key={group.clave} className="space-y-3" data-testid={`cat-group-${group.clave}`}>
            <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${group.config.bg} ${group.config.border}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${group.config.accent}`}>
                <CatIcon className={`w-4 h-4 ${group.config.color}`} />
              </div>
              <div className="flex-1">
                <h3 className={`text-sm font-semibold ${group.config.color}`}>{group.nombre}</h3>
                {group.descripcion && <p className="text-[10px] text-slate-500">{group.descripcion}</p>}
              </div>
              <span className="text-xs font-medium text-slate-500">{group.cursos.length} curso{group.cursos.length !== 1 ? 's' : ''}</span>
            </div>

            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {group.cursos.map(curso => (
                  <CursoCardSmall key={curso.id} curso={curso} status={getStatus(curso)} />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {group.cursos.map(curso => (
                  <CursoListItem key={curso.id} curso={curso} status={getStatus(curso)} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Sin cursos */}
      {cursos.length === 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <GraduationCap className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-medium text-slate-700">No tienes cursos asignados</h3>
            <p className="text-sm text-slate-500">Contacta a tu administrador</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Tarjeta pequeña para vista grid
const CursoCardSmall = ({ curso, status }) => {
  const defaultImage = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400';
  
  const statusColors = {
    pendiente: { badge: 'bg-amber-500', border: 'border-amber-200' },
    bloqueado: { badge: 'bg-slate-400', border: 'border-slate-200' },
    completado: { badge: 'bg-emerald-500', border: 'border-emerald-200' }
  };
  
  const colors = statusColors[status];

  return (
    <Link to={status !== 'bloqueado' ? `/portal/curso/${curso.id}` : '#'} className="block h-full">
      <Card className={`overflow-hidden hover:shadow-md transition-all duration-200 h-full flex flex-col ${status === 'bloqueado' ? 'opacity-60' : ''} border ${colors.border}`}>
        {/* Imagen pequeña */}
        <div className="relative h-24 overflow-hidden flex-shrink-0">
          <img 
            src={curso.imagen_url || defaultImage} 
            alt={curso.nombre}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = defaultImage; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          
          {/* Badge de estado */}
          <span className={`absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${colors.badge}`}>
            {status === 'pendiente' ? 'Pendiente' : status === 'completado' ? 'Aprobado' : <Lock className="w-3 h-3" />}
          </span>
          
          {/* Info sobre imagen */}
          <div className="absolute bottom-1.5 left-1.5 flex gap-1">
            {curso.horas && (
              <span className="text-[10px] text-white bg-black/50 px-1 py-0.5 rounded">
                {curso.horas}h
              </span>
            )}
            <span className={`text-[10px] text-white px-1 py-0.5 rounded ${
              curso.modalidad === 'e-learning' ? 'bg-blue-500/80' :
              curso.modalidad === 'presencial' ? 'bg-green-500/80' : 'bg-purple-500/80'
            }`}>
              {curso.modalidad === 'e-learning' ? 'Online' : curso.modalidad}
            </span>
          </div>
        </div>

        {/* Contenido */}
        <CardContent className="p-2 flex-1 flex flex-col">
          <h3 className="font-semibold text-xs text-slate-900 line-clamp-2 min-h-[32px] leading-tight">
            {curso.nombre}
          </h3>
          
          <div className="flex items-center justify-between mt-1.5 text-[10px] text-slate-500">
            <span>{curso.vigencia_meses ? `${curso.vigencia_meses}m` : '∞'}</span>
            <div className="flex gap-1">
              {curso.video_url && <Video className="w-3 h-3 text-blue-500" />}
              {curso.pdf_url && <FileText className="w-3 h-3 text-red-500" />}
              {curso.num_preguntas > 0 && <ClipboardList className="w-3 h-3 text-purple-500" />}
            </div>
          </div>
          
          {/* Siempre mostrar espacio para progreso - mantiene altura uniforme */}
          <div className="mt-auto pt-1.5">
            {status === 'completado' && curso.registro ? (
              <div className="text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                {curso.registro.nota}%
              </div>
            ) : (
              <div className="h-4"></div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

// Item de lista
const CursoListItem = ({ curso, status }) => {
  const defaultImage = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=100';
  
  const statusConfig = {
    pendiente: { badge: 'bg-amber-100 text-amber-700', text: 'Pendiente', btn: 'bg-teal-500 hover:bg-teal-600' },
    bloqueado: { badge: 'bg-slate-100 text-slate-500', text: 'Bloqueado', btn: 'bg-slate-300' },
    completado: { badge: 'bg-emerald-100 text-emerald-700', text: 'Completado', btn: 'bg-emerald-500 hover:bg-emerald-600' }
  };
  
  const config = statusConfig[status];

  return (
    <Card className={`border ${status === 'bloqueado' ? 'opacity-60 bg-slate-50' : 'bg-white'}`}>
      <CardContent className="p-3 flex items-center gap-3">
        {/* Imagen mini */}
        <img 
          src={curso.imagen_url || defaultImage} 
          alt={curso.nombre}
          className="w-16 h-12 object-cover rounded flex-shrink-0"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-sm text-slate-900 line-clamp-1">{curso.nombre}</h3>
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${config.badge}`}>
              {config.text}
            </span>
          </div>
          
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {curso.horas || '-'}h
            </span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${
              curso.modalidad === 'e-learning' ? 'bg-blue-50 text-blue-600' :
              curso.modalidad === 'presencial' ? 'bg-green-50 text-green-600' : 'bg-purple-50 text-purple-600'
            }`}>
              {curso.modalidad}
            </span>
            <span>
              <Calendar className="w-3 h-3 inline mr-0.5" />
              {curso.vigencia_meses ? `${curso.vigencia_meses}m` : 'Permanente'}
            </span>
            {curso.num_preguntas > 0 && (
              <span>
                <ClipboardList className="w-3 h-3 inline mr-0.5" />
                {curso.num_preguntas} preg.
              </span>
            )}
          </div>
        </div>
        
        {/* Recursos y botón */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex gap-1">
            {curso.video_url && <Video className="w-4 h-4 text-blue-500" />}
            {curso.pdf_url && <FileText className="w-4 h-4 text-red-500" />}
          </div>
          
          {status !== 'bloqueado' ? (
            <Link to={`/portal/curso/${curso.id}`}>
              <Button size="sm" className={`${config.btn} text-white h-7 text-xs px-2`}>
                {status === 'completado' ? 'Ver' : 'Iniciar'}
              </Button>
            </Link>
          ) : (
            <Button size="sm" disabled className="h-7 text-xs px-2">
              <Lock className="w-3 h-3" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
